/**
 * Ficha de una tarea en modo lectura, con edición campo por campo.
 *
 * El DOM va en orden cabecera → propiedades → textos (así se lee bien en el celular y con lector de
 * pantalla); en la vista ampliada el CSS pone las propiedades en una tarjeta fija a la derecha.
 *
 * Qué se puede tocar lo dicen las mismas funciones del contrato que usa la API
 * (`puedeEditarRequerimiento`, `cambiosPermitidos`): la web solo oculta lápices y botones, y la API
 * decide. Requerimiento: solo quien creó la tarea y ADMIN. Colaboración (estado, enlace, asignarse o
 * soltarse, comentar, adjuntar): cualquiera con escritura. LECTURA solo lee. La fecha límite (CDT-76) es parte del
 * requerimiento: se edita con el lápiz y un campo de fecha nativo; vaciarlo la quita.
 */
import type { ReactNode } from 'react';
import {
  ETIQUETA_TIPO,
  PRIORIDADES,
  TIPOS,
  cambiosPermitidos,
  enlaceHttpSchema,
  errorFechaInicio,
  errorFechaLimite,
  hoyEnChile,
  puedeEditarRequerimiento,
  type EventoTarea,
  type Prioridad,
  type Tarea,
  type TareaDetalle,
  type Tipo,
  type Usuario,
} from '@cdtec-backlog/contrato';
import { Avatar } from '../../componentes/Avatar';
import { Icono } from '../../componentes/Icono';
import { mensajeDeCampo } from '../../lib/api';
import { diaEnChile, diaLargo, fechaDiaChile, fechaHora } from '../../lib/formato';
import { useEtiquetaPrioridad } from '../configuracion/hooks';
import { esEnlaceRepoValido } from '../proyectos/EnlaceRepo';
import { SelectorProyecto } from '../proyectos/SelectorProyecto';
import { AvisoDescartada } from './AvisoDescartada';
import { SeccionBloqueos } from './Bloqueos';
import { AreaAutoAlto, CampoEditable } from './CampoEditable';
import { EtiquetaPrioridad } from './EtiquetasTarea';
import { MarcaFechaLimite, situacionFechaLimite } from './FechaLimite';
import { useActualizarTarea } from './hooks';
import { IconoTipo } from './IconoTipo';
import { EtiquetaEsfuerzo, SelectorEsfuerzo } from './SelectorEsfuerzo';
import { BloqueMadre, SeccionSubtareas } from './Subtareas';
import './VistaTarea.css';

interface Props {
  /**
   * La tarea; con `eventos` (el detalle), el aviso de descartada dice quién la descartó. Con `subtareas` (el
   * detalle) se muestran la tarea madre y la sección «Subtareas»; con `bloqueaA` (el detalle), los bloqueos.
   */
  tarea: Tarea & {
    eventos?: EventoTarea[];
    subtareas?: TareaDetalle['subtareas'];
    bloqueaA?: TareaDetalle['bloqueaA'];
  };
  personas: Usuario[];
  yoId: number;
  puedeEscribir: boolean;
  puedeAdministrar: boolean;
  /** Va al final de la columna de textos: adjuntos, comentarios, historial y eliminar. */
  children?: ReactNode;
  /** Abre otra tarea en la ficha (la madre o una subtarea). */
  onAbrir?: (id: number) => void;
  /** «Nueva subtarea»: abre «Nueva tarea» con esta como madre. */
  onNuevaSubtarea?: (madreId: number) => void;
}

const SIN_DATO = <span className="vista__vacio">—</span>;
const texto = (valor: string, vacio?: string) =>
  valor.trim() ? valor : <span className="vista__vacio">{vacio ?? '—'}</span>;

/** Una fecha nunca se parte en dos líneas. */
const Fecha = ({ texto }: { texto: string }) =>
  texto ? <span className="vista__fecha">{texto}</span> : SIN_DATO;

/** Solo enlaces http(s) son clicables; cualquier otra cosa se muestra como texto. */
function EnlaceSeguro({ valor }: { valor: string }) {
  if (!valor) return <span className="vista__vacio">Sin enlace</span>;
  let esHttp = false;
  try {
    esHttp = ['http:', 'https:'].includes(new URL(valor).protocol);
  } catch {
    esHttp = false;
  }
  return esHttp ? (
    <a href={valor} target="_blank" rel="noopener noreferrer">
      {valor}
    </a>
  ) : (
    <>{valor}</>
  );
}

/**
 * Una propiedad: etiqueta pequeña arriba y el valor abajo. `ancho` ocupa la fila completa siempre;
 * `fila` solo en la tarjeta de la vista ampliada (allí las propiedades cortas van de a dos).
 */
function Propiedad({
  titulo,
  ancho = false,
  fila = false,
  children,
}: {
  titulo: ReactNode;
  ancho?: boolean;
  fila?: boolean;
  children: ReactNode;
}) {
  const clases = ['vista__prop', ancho && 'vista__prop--ancho', fila && 'vista__prop--fila']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={clases}>
      <dt>{titulo}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Chip del proyecto: si tiene repo, todo el chip es el enlace (con ícono de «abrir afuera»). */
function ChipProyecto({ proyecto }: { proyecto: NonNullable<Tarea['proyecto']> }) {
  if (esEnlaceRepoValido(proyecto.enlaceRepo)) {
    return (
      <a
        className="etiqueta vista__chip vista__chip--enlace"
        href={proyecto.enlaceRepo}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Abrir el repo de ${proyecto.nombre}`}
        title="Abrir el repo"
      >
        {proyecto.nombre}
        <Icono nombre="fuera" tamano={12} />
      </a>
    );
  }
  return (
    <span className="etiqueta vista__chip">
      <span className="solo-lectores">Proyecto: </span>
      {proyecto.nombre}
    </span>
  );
}

export function VistaTarea({
  tarea,
  personas,
  yoId,
  puedeEscribir,
  puedeAdministrar,
  children,
  onAbrir,
  onNuevaSubtarea,
}: Props) {
  const etiquetaPrioridad = useEtiquetaPrioridad();
  const asignar = useActualizarTarea(tarea.id);
  const cambiarEsfuerzo = useActualizarTarea(tarea.id);

  // El rol exacto no importa: solo si es administrador y si puede escribir.
  const usuario = {
    id: yoId,
    rol: puedeAdministrar
      ? ('ADMIN' as const)
      : puedeEscribir
        ? ('DESARROLLO' as const)
        : ('LECTURA' as const),
  };
  const puedeReq = puedeEscribir && puedeEditarRequerimiento(usuario, tarea);
  const puedeAsignarme =
    puedeEscribir && cambiosPermitidos(usuario, tarea, { responsableId: yoId }).ok;
  // Las fechas de inicio y de cierre son solo de ADMIN: ni quien creó la tarea las edita.
  const puedeFechas = puedeEscribir && cambiosPermitidos(usuario, tarea, { creadoEn: '' }).ok;
  const inicioDia = diaEnChile(tarea.creadoEn);
  const cierreDia = diaEnChile(tarea.cerradaEn);
  // «Hoy» en Chile, con la misma función del contrato que usa la API para «atrasada».
  const hoy = hoyEnChile();
  const situacionLimite = situacionFechaLimite(tarea, hoy);
  const avisoLimite = situacionLimite === 'atrasada' || situacionLimite === 'pronto';
  const esResponsable = tarea.responsable?.id === yoId;
  const puedeSoltar =
    puedeEscribir && esResponsable && cambiosPermitidos(usuario, tarea, { responsableId: null }).ok;

  // Madre y subtareas solo con el detalle (la lista no trae `subtareas`).
  const detalle = tarea.subtareas ? { ...tarea, subtareas: tarea.subtareas } : null;

  const idAviso = `aviso-requerimiento-${tarea.id}`;
  const hayAviso = puedeEscribir && !puedeReq;
  const esfuerzoMostrado =
    cambiarEsfuerzo.isPending && cambiarEsfuerzo.variables?.esfuerzo
      ? cambiarEsfuerzo.variables.esfuerzo
      : tarea.esfuerzo;

  return (
    <div className="vista">
      <AvisoDescartada tarea={tarea} eventos={tarea.eventos} puedeEscribir={puedeEscribir} />
      <div className="vista__cab">
        <CampoEditable
          tareaId={tarea.id}
          etiqueta="Título"
          ocultarEtiqueta
          variante="titulo"
          puedeEditar={puedeReq}
          valor={tarea.titulo}
          vista={<h2 className="vista__titulo">{tarea.titulo}</h2>}
          cambios={(titulo) => ({ titulo: titulo.trim() })}
          validar={(titulo) => (titulo.trim() ? null : 'El título es obligatorio.')}
          control={({ id, borrador, cambiar, propsError }) => (
            <input
              id={id}
              type="text"
              value={borrador}
              maxLength={200}
              onChange={(e) => cambiar(e.target.value)}
              {...propsError}
            />
          )}
        />
        <div className="vista__chips">
          <EtiquetaPrioridad prioridad={tarea.prioridad} />
          <span className="etiqueta vista__chip">{ETIQUETA_TIPO[tarea.tipo]}</span>
          {tarea.proyecto && <ChipProyecto proyecto={tarea.proyecto} />}
          {tarea.tocaRiego && (
            <span className="etiqueta etiqueta--riego vista__chip">Toca riego</span>
          )}
          {tarea.requiereProduccion && (
            <span className="etiqueta etiqueta--produccion vista__chip">
              Pide algo de producción
            </span>
          )}
        </div>
        {hayAviso && (
          <p id={idAviso} className="vista__aviso">
            {tarea.creadaPor ? `Creada por ${tarea.creadaPor.nombre}. ` : ''}
            Solo {tarea.creadaPor ? 'quien la creó o ' : ''}un administrador puede cambiar el
            requerimiento; puedes dejar un comentario.
          </p>
        )}
        {detalle && <BloqueMadre tarea={detalle} puedeEditar={puedeReq} onAbrir={onAbrir} />}
      </div>

      <dl className="vista__props">
        <Propiedad titulo="Prioridad">
          <CampoEditable<Prioridad>
            tareaId={tarea.id}
            etiqueta="Prioridad"
            ocultarEtiqueta
            puedeEditar={puedeReq}
            valor={tarea.prioridad}
            vista={etiquetaPrioridad(tarea.prioridad)}
            cambios={(prioridad) => ({ prioridad })}
            control={({ id, borrador, cambiar }) => (
              <select
                id={id}
                value={borrador}
                onChange={(e) => cambiar(e.target.value as Prioridad)}
              >
                {PRIORIDADES.map((p) => (
                  <option key={p} value={p}>
                    {etiquetaPrioridad(p)}
                  </option>
                ))}
              </select>
            )}
          />
        </Propiedad>

        <Propiedad titulo="Tipo">
          <CampoEditable<Tipo>
            tareaId={tarea.id}
            etiqueta="Tipo"
            ocultarEtiqueta
            puedeEditar={puedeReq}
            valor={tarea.tipo}
            vista={
              <span className="vista__con-icono">
                <IconoTipo tipo={tarea.tipo} decorativo />
                <span title={ETIQUETA_TIPO[tarea.tipo]}>{ETIQUETA_TIPO[tarea.tipo]}</span>
              </span>
            }
            cambios={(tipo) => ({ tipo })}
            control={({ id, borrador, cambiar }) => (
              <select id={id} value={borrador} onChange={(e) => cambiar(e.target.value as Tipo)}>
                {TIPOS.map((t) => (
                  <option key={t} value={t}>
                    {ETIQUETA_TIPO[t]}
                  </option>
                ))}
              </select>
            )}
          />
        </Propiedad>

        <Propiedad titulo="Responsable" fila>
          <CampoEditable
            tareaId={tarea.id}
            etiqueta="Responsable"
            ocultarEtiqueta
            puedeEditar={puedeReq}
            valor={tarea.responsable ? String(tarea.responsable.id) : ''}
            vista={
              <span className="vista__responsable">
                {tarea.responsable ? (
                  <span className="vista__persona">
                    <Avatar nombre={tarea.responsable.nombre} />
                    <span title={tarea.responsable.nombre}>{tarea.responsable.nombre}</span>
                  </span>
                ) : (
                  <span className="vista__vacio">Sin asignar</span>
                )}
                {puedeSoltar ? (
                  <button
                    type="button"
                    className="vista__accion vista__accion--soltar"
                    disabled={asignar.isPending}
                    onClick={() => asignar.mutate({ responsableId: null })}
                  >
                    Soltar
                  </button>
                ) : puedeAsignarme && !esResponsable ? (
                  <button
                    type="button"
                    className="vista__accion"
                    disabled={asignar.isPending}
                    onClick={() => asignar.mutate({ responsableId: yoId })}
                  >
                    Asignarme
                  </button>
                ) : null}
              </span>
            }
            cambios={(valor) => ({ responsableId: valor === '' ? null : Number(valor) })}
            control={({ id, borrador, cambiar }) => (
              <select id={id} value={borrador} onChange={(e) => cambiar(e.target.value)}>
                <option value="">Sin asignar</option>
                {personas
                  .filter((p) => p.activo || String(p.id) === borrador)
                  .map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.nombre}
                    </option>
                  ))}
              </select>
            )}
          />
        </Propiedad>

        <Propiedad titulo="Proyecto" fila>
          <CampoEditable
            tareaId={tarea.id}
            etiqueta="Proyecto"
            ocultarEtiqueta
            puedeEditar={puedeReq}
            valor={tarea.proyecto ? String(tarea.proyecto.id) : ''}
            vista={
              tarea.proyecto ? (
                tarea.proyecto.nombre
              ) : (
                <span className="vista__vacio">Sin proyecto</span>
              )
            }
            cambios={(valor) => ({ proyectoId: valor === '' ? null : Number(valor) })}
            controlConEtiqueta
            control={({ borrador, cambiar, guardarCon, ocultarAcciones }) => (
              // Un solo juego de botones: con el bloque «Nuevo proyecto» abierto se ocultan Guardar/Cancelar
              // del campo y «Agregar» crea el proyecto y guarda la tarea en un paso.
              <SelectorProyecto
                valor={borrador}
                onCambio={cambiar}
                puedeAgregar
                etiquetaOculta
                onAgregado={guardarCon}
                onBloqueNuevo={ocultarAcciones}
              />
            )}
          />
        </Propiedad>

        <Propiedad titulo="Pedido por">
          <CampoEditable
            tareaId={tarea.id}
            etiqueta="Pedido por"
            ocultarEtiqueta
            puedeEditar={puedeReq}
            valor={tarea.pedidoPor}
            vista={texto(tarea.pedidoPor)}
            cambios={(pedidoPor) => ({ pedidoPor })}
            control={({ id, borrador, cambiar }) => (
              <input
                id={id}
                type="text"
                value={borrador}
                maxLength={80}
                onChange={(e) => cambiar(e.target.value)}
              />
            )}
          />
        </Propiedad>

        <Propiedad titulo="Fecha límite">
          <CampoEditable
            tareaId={tarea.id}
            etiqueta="Fecha límite"
            ocultarEtiqueta
            puedeEditar={puedeReq}
            valor={tarea.fechaLimite ?? ''}
            vista={
              tarea.fechaLimite ? (
                <span className="vista__limite">
                  <Fecha texto={diaLargo(tarea.fechaLimite)} />
                  {/* Solo la marca de aviso (atrasada o vence pronto); la fecha ya está escrita al lado. */}
                  {avisoLimite && <MarcaFechaLimite tarea={tarea} hoy={hoy} />}
                </span>
              ) : (
                <span className="vista__vacio">Sin fecha límite</span>
              )
            }
            cambios={(dia) => ({ fechaLimite: dia || null })}
            validar={(dia) => (dia ? errorFechaLimite(dia) : null)}
            mensajeError={mensajeDeCampo}
            control={({ id, borrador, cambiar, propsError }) => (
              <input
                id={id}
                // Sin min/max: el rango lo valida `errorFechaLimite` con su mensaje (el del navegador no se controla).
                type="date"
                value={borrador}
                onChange={(e) => cambiar(e.target.value)}
                {...propsError}
              />
            )}
          />
        </Propiedad>

        <Propiedad titulo="Creada por">
          {tarea.creadaPor ? (
            <span className="vista__persona">
              <Avatar nombre={tarea.creadaPor.nombre} />
              <span title={tarea.creadaPor.nombre}>{tarea.creadaPor.nombre}</span>
            </span>
          ) : (
            <span className="vista__vacio">Sin creador registrado</span>
          )}
        </Propiedad>

        <Propiedad titulo="Inicio">
          <CampoEditable
            tareaId={tarea.id}
            etiqueta="Inicio"
            ocultarEtiqueta
            nombreLapiz="Editar fecha de inicio"
            puedeEditar={puedeFechas}
            valor={inicioDia}
            vista={<Fecha texto={fechaDiaChile(tarea.creadoEn)} />}
            cambios={(creadoEn) => ({ creadoEn })}
            validar={(dia) => (dia ? errorFechaInicio(dia) : 'Elige una fecha de inicio.')}
            mensajeError={mensajeDeCampo}
            control={({ id, borrador, cambiar, propsError }) => (
              <input
                id={id}
                type="date"
                value={borrador}
                max={hoy}
                onChange={(e) => cambiar(e.target.value)}
                {...propsError}
              />
            )}
          />
        </Propiedad>

        <Propiedad titulo="Actualizada">
          <Fecha texto={fechaHora(tarea.actualizadoEn)} />
        </Propiedad>

        {tarea.estado === 'HECHO' && (
          <Propiedad titulo="Cierre">
            <CampoEditable
              tareaId={tarea.id}
              etiqueta="Cierre"
              ocultarEtiqueta
              nombreLapiz="Editar fecha de cierre"
              puedeEditar={puedeFechas}
              valor={cierreDia}
              vista={<Fecha texto={fechaDiaChile(tarea.cerradaEn)} />}
              cambios={(cerradaEn) => ({ cerradaEn })}
              validar={(dia) => {
                if (!dia) return 'Elige una fecha de cierre.';
                const error = errorFechaInicio(dia);
                if (error) return error;
                return dia < inicioDia
                  ? 'La fecha de cierre no puede ser anterior a la fecha de inicio.'
                  : null;
              }}
              mensajeError={mensajeDeCampo}
              control={({ id, borrador, cambiar, propsError }) => (
                <input
                  id={id}
                  type="date"
                  value={borrador}
                  min={inicioDia || undefined}
                  max={hoy}
                  onChange={(e) => cambiar(e.target.value)}
                  {...propsError}
                />
              )}
            />
          </Propiedad>
        )}

        <Propiedad titulo="Toca riego">
          <CampoEditable<boolean>
            tareaId={tarea.id}
            etiqueta="Toca riego"
            ocultarEtiqueta
            puedeEditar={puedeReq}
            valor={tarea.tocaRiego}
            vista={tarea.tocaRiego ? 'Sí (revisión de seguridad obligatoria)' : 'No'}
            cambios={(tocaRiego) => ({ tocaRiego })}
            controlConEtiqueta
            control={({ id, borrador, cambiar }) => (
              <label className="cedit__casilla" htmlFor={id}>
                <input
                  id={id}
                  type="checkbox"
                  checked={borrador}
                  onChange={(e) => cambiar(e.target.checked)}
                />
                Toca riego (revisión de seguridad obligatoria)
              </label>
            )}
          />
        </Propiedad>

        <Propiedad titulo="Pide algo a producción">
          <CampoEditable<boolean>
            tareaId={tarea.id}
            etiqueta="Pide algo a producción"
            ocultarEtiqueta
            puedeEditar={puedeReq}
            valor={tarea.requiereProduccion}
            vista={tarea.requiereProduccion ? 'Sí' : 'No'}
            cambios={(requiereProduccion) => ({ requiereProduccion })}
            controlConEtiqueta
            control={({ id, borrador, cambiar }) => (
              <label className="cedit__casilla" htmlFor={id}>
                <input
                  id={id}
                  type="checkbox"
                  checked={borrador}
                  onChange={(e) => cambiar(e.target.checked)}
                />
                Pide algo a producción
              </label>
            )}
          />
        </Propiedad>

        <Propiedad titulo={<EtiquetaEsfuerzo />} ancho>
          <SelectorEsfuerzo
            valor={esfuerzoMostrado}
            deshabilitado={!puedeReq || cambiarEsfuerzo.isPending}
            descritoPor={hayAviso ? idAviso : undefined}
            onElegir={(esfuerzo) => cambiarEsfuerzo.mutate({ esfuerzo })}
          />
        </Propiedad>
      </dl>

      <div className="vista__textos">
        {detalle && (
          <SeccionSubtareas
            tarea={detalle}
            quienMira={usuario}
            puedeEscribir={puedeEscribir}
            onAbrir={onAbrir}
            onNuevaSubtarea={onNuevaSubtarea}
          />
        )}
        {tarea.bloqueaA && (
          <SeccionBloqueos
            tarea={{ ...tarea, bloqueaA: tarea.bloqueaA }}
            puedeEscribir={puedeEscribir}
            onAbrir={onAbrir}
          />
        )}
        <CampoEditable
          tareaId={tarea.id}
          etiqueta="Qué se necesita y para qué"
          variante="seccion"
          nombreLapiz="Editar descripción"
          puedeEditar={puedeReq}
          valor={tarea.descripcion}
          vista={texto(tarea.descripcion, 'Sin descripción')}
          cambios={(descripcion) => ({ descripcion })}
          control={({ id, borrador, cambiar, propsError }) => (
            <AreaAutoAlto
              id={id}
              value={borrador}
              maxLength={5000}
              onChange={(e) => cambiar(e.target.value)}
              {...propsError}
            />
          )}
        />

        <CampoEditable
          tareaId={tarea.id}
          etiqueta="Cómo se comprueba que está listo"
          variante="seccion"
          nombreLapiz="Editar criterio de aceptación"
          puedeEditar={puedeReq}
          valor={tarea.criterio}
          vista={texto(tarea.criterio, 'Sin criterio de aceptación')}
          cambios={(criterio) => ({ criterio })}
          control={({ id, borrador, cambiar, propsError }) => (
            <AreaAutoAlto
              id={id}
              value={borrador}
              maxLength={2000}
              onChange={(e) => cambiar(e.target.value)}
              {...propsError}
            />
          )}
        />

        {(tarea.requiereProduccion || tarea.detalleProduccion.trim() !== '') && (
          <CampoEditable
            tareaId={tarea.id}
            etiqueta="Qué pide a producción"
            variante="seccion"
            puedeEditar={puedeReq}
            valor={tarea.detalleProduccion}
            vista={texto(tarea.detalleProduccion)}
            cambios={(detalleProduccion) => ({ detalleProduccion })}
            control={({ id, borrador, cambiar }) => (
              <AreaAutoAlto
                id={id}
                value={borrador}
                maxLength={300}
                onChange={(e) => cambiar(e.target.value)}
              />
            )}
          />
        )}

        <CampoEditable
          tareaId={tarea.id}
          etiqueta="Enlace (PR, issue o documento)"
          variante="seccion"
          nombreLapiz="Editar enlace"
          puedeEditar={puedeEscribir}
          valor={tarea.enlace}
          vista={<EnlaceSeguro valor={tarea.enlace} />}
          cambios={(enlace) => ({ enlace: enlace.trim() })}
          validar={(enlace) => {
            const analisis = enlaceHttpSchema.safeParse(enlace);
            return analisis.success
              ? null
              : (analisis.error.issues[0]?.message ?? 'Enlace inválido.');
          }}
          control={({ id, borrador, cambiar, propsError }) => (
            <input
              id={id}
              type="url"
              value={borrador}
              maxLength={500}
              placeholder="https://github.com/…"
              onChange={(e) => cambiar(e.target.value)}
              {...propsError}
            />
          )}
        />

        {children}
      </div>
    </div>
  );
}
